import { GenericInputProps } from 'components/ui/GenericInput';

interface Settings {
  id: number;
  wallet: string;
  city: number;
  state: number;
  country: number;
  language: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface BaseCompany {
  id: number;
  name: string;
  address: string;
  totalTxs: number;
  availableTxs: number;
  type: string;
  chainNumber: string;
  contactName: string;
  taxIDNumber: string | number;
  settings: Settings;
  typesOfFood: number[];
  createdAt: string;
  updatedAt: string | null;
  email: string;
  phone: string;
}

export interface CompanyById extends BaseCompany {
  fiscalId: string | null;
  customCode: string;
}

export interface CreateCompanyRequest {
  name: string;
  email?: string;
  isDarwin?: boolean;
  wallet?: string;
  txHash?: string;
  chainNumber?: string;
  address?: string;
  fiscalId?: string;
  type?: string;
  totalTxs?: number;
  availableTxs?: number;
  taxIDNumber?: string;
  contactName?: string;
  contactNumber?: string;
  phone?: string;
  cityId?: number;
  language?: string;
  detailLocation?: string;
  zipCode?: string;
  timeZoneId?: number;
}

export interface CreateCompanyAllianceRequest {
  companyId: number;
  partnerCompanyId: number;
}

export interface AssignableCompany {
  id: string;
  name: string;
  contractAddress: string;
}

export enum DataSharingStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export interface Company {
  id: string;
  name: string;
  description: string;
  fiscalId: string;
  organizationKey: string;
  isPrimary: boolean;
  addressCount: number;
  locationCount: number;
  dataSharing?: {
    status: DataSharingStatus;
  };
}

export type OrganizationDetailSectionFormData = {
  id?: string;
  name: string;
  fiscalId: string;
  description: string;
};

export type OrganizationInputProps = Array<
  Omit<
    GenericInputProps<OrganizationDetailSectionFormData>,
    'control' | 'errors'
  >
>;

export type AddressBookSectionInputProps = Array<
  Omit<GenericInputProps<AddressBookSectionFormData>, 'control' | 'errors'>
>;

interface CompanyAddressForm {
  addressName?: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  contactName: string;
  contactPhone?: { phoneCode: string; phoneNumber: string };
  contactEmail?: string;
}

export interface CreateExternalCompany {
  name: string;
  description?: string;
  fiscalId: string;
  contractAddress: string;
  txHash?: string;
  isEudrEnabled?: boolean;
  eoriNumber?: string;
  propertyDocuments?: string[];
  noDeforestation?: string[];
  signedStatement?: string[];
}

export type AddressBookSectionFormData = CompanyAddressForm;

export interface CreateAddressPayload {
  addressName?: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  contact: {
    contactName: string;
    phoneCode: string;
    phoneNumber: string;
    contactEmail?: string;
  };
}

export interface OrganizationModalApprove {
  id: string;
  name: string;
  description: string;
  fiscalId: string;
  organizationKey: string | null;
  isPrimary: boolean;
  contractAddress: string;
  txHash: string | null;
  createdAt: string;
  updatedAt: string;
  addresses: CompanyAddress[];
}

export interface CreateCompanyResponse {
  name: string;
  description: string;
  fiscalId: string;
  contractAddress: string;
  isPrimary: boolean;
  account: Account;
  organizationKey: string | null;
  txHash: string | null;
  id: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface UploadLocationImageResponse {
  s3Key: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  bucketName: string;
  uploadedBy: string;
  entityType: string | null;
  entityId: string | null;
  id: string;
  uploadedAt: string | Date;
}

interface Account {
  id: string;
  name: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CreateCompanyAddressResponse {
  id: string;
  addressName: string;
  isPrimary: boolean;
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  city: string;
  description: string | null;
  zipCode: string;
  active: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CompanyAddress {
  id: string;
  active: boolean;
  addressName: string;
  isPrimary: boolean;
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  city: string;
  description: string | null;
  zipCode: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  contacts: Contact[];
  locations: Location[];
}

interface Contact {
  id: string;
  contactName: string;
  phoneCode: string;
  phoneNumber: string;
  contactEmail: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface Metadata {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

export interface Location {
  id: string;
  name: string;
  description: string;
  externalUrl?: string;
  fileUrl?: string;
  process: {
    id: string;
    name: string;
  };
  externalId?: string;
}

export interface CreateLocationPayload {
  name: string;
  description: string;
  processId: string;
  externalId?: string;
  externalUrl?: string;
  fileUrl?: string;
}

export interface DataSharingRequestPayload {
  reason: string;
}

export type DataSharingProps = Array<
  Omit<GenericInputProps<DataSharingRequestPayload>, 'control' | 'errors'>
>;

export type LocationInputProps = Array<
  Omit<GenericInputProps<CreateLocationPayload>, 'control' | 'errors'>
>;

export interface DataSharingPayload {
  contactName: string;
  phone: string;
  email: string;
  reason: string;
}
